import { useEffect, useState } from "react";
import { CreditCard, Wallet, Repeat } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { StatusPill } from "../trainer/TrainerSchedule";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../lib/useCurrency";

function nextBillDate() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

export default function ClientInvoices() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [invoices, setInvoices] = useState([]);
  const [balance, setBalance] = useState(0);
  const [plan, setPlan] = useState(user?.monthly_plan || null);
  const [paying, setPaying] = useState(null);

  const load = async () => {
    try {
      const [inv, cr, me] = await Promise.all([
        api.get("/invoices"),
        api.get("/me/credits"),
        api.get("/auth/me"),
      ]);
      setInvoices(inv.data);
      setBalance(Number(cr.data.balance || 0));
      setPlan(me.data?.monthly_plan || null);
    } catch (_e) {}
  };

  useEffect(() => { load(); }, []);

  const pay = async (id) => {
    setPaying(id);
    try {
      const { data } = await api.post("/payments/checkout", { invoice_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPaying(null);
    }
  };

  const planActive = plan && plan.active && Number(plan.amount || 0) > 0;
  const nb = nextBillDate();

  return (
    <div className="space-y-8" data-testid="client-invoices">
      <div>
        <div className="tracker text-brand mb-3">[ Billing ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Payments.</h1>
        <p className="text-zinc-400 mt-2 text-sm">Each payment tops up your pot. Sessions are deducted automatically when your trainer books them.</p>
      </div>

      <div className="card-flat p-6 flex items-center justify-between flex-wrap gap-4" data-testid="invoices-pot">
        <div className="flex items-center gap-3">
          <Wallet size={28} weight="duotone" className="text-brand" />
          <div>
            <div className="tracker">Current pot</div>
            <div className="font-display text-3xl money">{symbol}{balance.toFixed(2)}</div>
          </div>
        </div>
        {planActive && (
          <div className="flex items-center gap-2 tracker text-zinc-400" data-testid="invoice-plan-line">
            <Repeat size={14} weight="bold" className="text-brand" />
            <span>
              <span className="text-white font-mono money">{symbol}{Number(plan.amount).toFixed(2)}/mo</span>
              {" "}· next bill{" "}
              <span className="text-white">{nb.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
            </span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {invoices.length === 0 ? (
          <div className="card-flat p-16 text-center text-zinc-500 tracker">No payments yet</div>
        ) : (
          invoices.map((inv) => (
            <div key={inv.id} className="card-flat p-6 flex items-center gap-6 flex-wrap" data-testid={`client-invoice-${inv.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="tracker mb-1 flex items-center gap-2">
                  {new Date(inv.created_at).toLocaleDateString()}
                  {inv.prorated && <span className="px-2 py-0.5 border border-brand/40 text-brand-light text-[10px]">PRORATED</span>}
                  {inv.auto_generated && !inv.prorated && <span className="px-2 py-0.5 border border-white/10 text-zinc-500 text-[10px]">AUTO</span>}
                </div>
                <div className="font-display text-2xl">{inv.description}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-3xl money">{inv.currency?.toUpperCase()} {inv.amount.toFixed(2)}</div>
                {inv.vat_amount ? (
                  <div className="tracker text-[9px] text-zinc-500 mt-1" data-testid={`client-invoice-vat-${inv.id}`}>
                    incl. {inv.vat_percent}% VAT ({inv.currency?.toUpperCase()} {Number(inv.vat_amount).toFixed(2)})
                    {inv.vat_number ? <div className="mt-0.5 font-mono">VAT No. {inv.vat_number}</div> : null}
                  </div>
                ) : null}
                <div className="mt-2"><StatusPill status={inv.status} /></div>
              </div>
              {inv.status === "unpaid" && (
                <button onClick={() => pay(inv.id)} disabled={paying === inv.id} className="btn-primary" data-testid={`pay-invoice-${inv.id}`}>
                  <CreditCard size={16} weight="duotone" /> {paying === inv.id ? "Redirecting…" : "Top up pot"}
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
